import {
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  createTableColumn,
  FluentProvider,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  TableCellLayout,
  makeStyles,
  tokens,
  Button,
  Tooltip,
  type DataGridProps,
} from '@fluentui/react-components';
import { useActiveTheme } from '../theme/ThemeContext';
import {
  ArrowSort20Regular,
  ArrowSortUp20Filled,
  ArrowSortDown20Filled,
  Info16Regular,
  MoreVertical20Regular,
} from '@fluentui/react-icons';
import React, { useCallback, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { formatDate } from '@moko/utils';
import EllipsisWithTooltip from '../EllipsisWithTooltip/EllipsisWithTooltip';
import LoadingSign from '../LoadingSign/LoadingSign';
import type { EmptyStateConfig } from '../Empty/Empty';
import { renderEmptyState } from '../Empty/renderEmptyState';
import type { IColumnFilterConfig } from './Filter';

const useStyles = makeStyles({
  scroll: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'auto',
    boxSizing: 'border-box',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
  },
  gridFill: {
    width: '100%',
    maxWidth: '100%',
  },
  gridFree: {
    width: 'max-content',
    minWidth: '100%',
    '& .fui-DataGridRow > :last-child .fui-TableResizeHandle': {
      marginRight: '0px',
    },
  },
  lastColumn: {
    flexGrow: 1,
    maxWidth: 'none !important',
  },
  rowActionsHeader: {
    position: 'sticky',
    right: 0,
    zIndex: 2,
    width: '25px',
    maxWidth: '25px',
    minWidth: '25px',
    padding: '5px',
    backgroundColor: tokens.colorBrandBackground,
  },
  rowActionsCell: {
    position: 'sticky',
    right: 0,
    zIndex: 1,
    width: '25px',
    maxWidth: '25px',
    minWidth: '25px',
    padding: '5px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    backgroundColor: tokens.colorBrandBackground,
    '& .fui-TableSelectionCell, & .fui-DataGridHeaderCell, & .fui-TableHeaderCell':
      {
        backgroundColor: tokens.colorBrandBackground,
        backgroundImage: 'none',
        color: tokens.colorNeutralForegroundOnBrand,
      },
    '& .fui-TableResizeHandle::after': {
      backgroundColor: tokens.colorNeutralForegroundOnBrand,
    },
    '& .fui-Checkbox': {
      color: tokens.colorNeutralForegroundOnBrand,
      '--fui-Checkbox__indicator--borderColor':
        tokens.colorNeutralForegroundOnBrand,
      '--fui-Checkbox__indicator--backgroundColor': 'transparent',
      '--fui-Checkbox__indicator--color': tokens.colorBrandBackground,
    },
    '& .fui-Checkbox:hover, & .fui-Checkbox:active': {
      color: tokens.colorNeutralForegroundOnBrand,
      '--fui-Checkbox__indicator--borderColor':
        tokens.colorNeutralForegroundOnBrand,
    },
    '& .fui-Checkbox:has(.fui-Checkbox__input:checked), & .fui-Checkbox:has(.fui-Checkbox__input:indeterminate)':
      {
        '--fui-Checkbox__indicator--borderColor':
          tokens.colorNeutralForegroundOnBrand,
        '--fui-Checkbox__indicator--backgroundColor':
          tokens.colorNeutralForegroundOnBrand,
        '--fui-Checkbox__indicator--color': tokens.colorBrandBackground,
      },
  },
  headerCell: {
    backgroundColor: tokens.colorBrandBackground,
    backgroundImage: 'none',
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
    borderInlineEndWidth: 0,
    borderInlineEndColor: 'transparent',
    '& svg': {
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
  headerLabel: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
  },
  headerHint: {
    background: 'none',
    border: 'none',
    padding: 0,
    marginLeft: tokens.spacingHorizontalXXS,
    cursor: 'help',
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    color: 'inherit',
  },
});

const ColumnHint = ({ hint }: { hint: string }) => {
  const styles = useStyles();
  return (
    <Tooltip content={hint} relationship="description" withArrow>
      <button
        type="button"
        className={styles.headerHint}
        aria-label={hint}
        onClick={(e) => e.stopPropagation()}
      >
        <Info16Regular />
      </button>
    </Tooltip>
  );
};

export type SortDirection = 'ascending' | 'descending';

export interface DataGridColumn<T> {
  key: string;
  name?: string;
  /** Initial column width in pixels. Omit for auto sizing. */
  width?: number;
  /** Minimum column width when resizing or shrinking. */
  minWidth?: number;
  hideOnSmallScreen?: boolean;
  hidden?: boolean;
  isSortable?: boolean;
  ellipsis?: boolean;
  /** When set to 'date'/'datetime', the raw value is formatted with month name (e.g. "May 8, 2026"). */
  type?: 'date' | 'datetime';
  filter?: IColumnFilterConfig;
  hint?: string;
  value?(item: T): string;
  renderCell?(item: T): React.ReactNode;
}

/** Loading configuration */
export interface LoadingConfig {
  /** Loading message (default: "Loading ...") */
  message?: string;
  /** Icon element to display */
  icon?: React.ReactNode;
}

/**
 * Per-row context-menu option. When `rowMenuOptions` is provided to a list
 * component (DataGridUI / PagedDataGrid / PagedCardList), clicking a row
 * opens a Fluent UI v9 menu populated with these options. Selection mode
 * is implicitly disabled in this case.
 */
export interface IRowMenuOption<T> {
  key: string;
  label: string;
  icon?: React.JSX.Element;
  disabled?: boolean | ((item: T) => boolean);
  onClick: (item: T, context: { refresh: () => void }) => void;
}

export interface DataGridUIProps<T> {
  /** Data items to display */
  items: T[];
  /** Column definitions */
  columns: DataGridColumn<T>[];
  /** Show loading state */
  loading?: boolean;
  /** Loading configuration (message and icon) */
  loadingConfig?: LoadingConfig;
  /** Fixed height (enables vertical scroll) */
  height?: number;
  /** Selection mode: "single", "multiselect", or undefined for none */
  selectionMode?: DataGridProps['selectionMode'];
  /** Show drag handles on column edges (default: true). */
  resizableColumns?: boolean;
  /**
   * When true the table always fills 100% of its container and resizing
   * borrows space from neighbouring columns. When false (default) the table
   * sizes to its columns' widths and columns can be resized freely past the
   * container, with the wrapper scrolling horizontally.
   */
  fillContainer?: boolean;
  /** Currently sorted column (e.g., "id" or "id Desc") */
  sortedColumn?: string;
  /** Get unique ID for each row */
  getRowId: (item: T) => string | number;
  /** Called when selection changes */
  onSelectionChange?: (items: T[]) => void;
  /**
   * Controlled selection: when provided, the grid uses this set as the
   * source of truth for which rows are checked. Pair with
   * `onSelectionChange` to manage selection in the parent (e.g. to persist
   * across pagination). Only ids present in the current `items` array are
   * shown as checked.
   */
  selectedIds?: Set<string | number>;
  /** Called when row is double-clicked */
  onRowDoubleClick?: (item: T) => void;
  /** Called when sortable column header is clicked */
  onColumnSort?: (columnKey: string) => void;
  /**
   * When provided, clicking a row opens a context menu populated with
   * these options. This is independent of `selectionMode` — consumers who
   * want to opt out of selection should simply not pass `selectionMode` /
   * `onSelectionChange`. Double-click navigation is suppressed when the
   * menu is active.
   */
  rowMenuOptions?: IRowMenuOption<T>[];
  /** Called when consumers need to refresh the underlying data (passed to row menu onClick) */
  onRowMenuRefresh?: () => void;
  empty?: EmptyStateConfig;
}

// ============================================================================
// Component
// ============================================================================

function DataGridUI<T>({
  items,
  columns,
  loading = false,
  loadingConfig,
  height,
  selectionMode,
  resizableColumns = true,
  fillContainer = false,
  sortedColumn,
  getRowId,
  onSelectionChange,
  onRowDoubleClick,
  onColumnSort,
  rowMenuOptions,
  onRowMenuRefresh,
  selectedIds: controlledSelectedIds,
  empty,
}: DataGridUIProps<T>): React.ReactElement {
  const theme = useActiveTheme();
  const styles = useStyles();
  const hasRowMenu = !!rowMenuOptions && rowMenuOptions.length > 0;

  // Row menu state — anchored to the click coordinates via a Fluent UI
  // virtual positioning target so the menu opens at the cursor.
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuItem, setMenuItem] = useState<T | null>(null);
  const [menuVirtualTarget, setMenuVirtualTarget] = useState<{
    getBoundingClientRect: () => DOMRect;
  } | null>(null);

  const handleMenuOptionClick = useCallback(
    (option: IRowMenuOption<T>) => {
      if (!menuItem) return;
      option.onClick(menuItem, { refresh: () => onRowMenuRefresh?.() });
      setMenuOpen(false);
    },
    [menuItem, onRowMenuRefresh]
  );

  // Responsive: filter columns on small screens
  const isSmallScreen = useMediaQuery({ maxWidth: 767 });
  const visibleColumns = useMemo(
    () =>
      columns
        .filter((col) => !col.hidden)
        .filter((col) => !isSmallScreen || !col.hideOnSmallScreen),
    [isSmallScreen, columns]
  );

  // Selection state — controlled when `selectedIds` prop is provided,
  // otherwise we manage it locally for backward compatibility.
  const isControlledSelection = controlledSelectedIds !== undefined;
  const [internalSelectedIds, setInternalSelectedIds] = useState<
    Set<string | number>
  >(new Set());
  const selectedIds = isControlledSelection
    ? controlledSelectedIds
    : internalSelectedIds;

  // Selection handler
  const handleSelectionChange = useCallback(
    (newSelectedIds: Set<string | number>) => {
      if (!isControlledSelection) {
        setInternalSelectedIds(newSelectedIds);
      }
      if (!onSelectionChange) return;
      const selected = items.filter((item) =>
        newSelectedIds.has(getRowId(item))
      );
      onSelectionChange(selected);
    },
    [items, getRowId, onSelectionChange, isControlledSelection]
  );

  // Column click handler
  const handleColumnClick = useCallback(
    (columnKey: string) => onColumnSort?.(columnKey),
    [onColumnSort]
  );

  // Open the menu anchored at the click coordinates of the trigger button.
  const openRowMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>, item: T) => {
      // Stop propagation so Fluent's row onClick (which would also toggle
      // the row's selection when selectionMode is on) never fires.
      e.stopPropagation();
      const x = e.clientX;
      const y = e.clientY;
      setMenuVirtualTarget({
        getBoundingClientRect: () =>
          ({
            x,
            y,
            top: y,
            left: x,
            right: x,
            bottom: y,
            width: 0,
            height: 0,
            toJSON: () => ({}),
          } as DOMRect),
      });
      setMenuItem(item);
      setMenuOpen(true);
    },
    []
  );

  // Build FluentUI columns
  const gridColumns = useMemo(() => {
    const dataCols = visibleColumns.map((column) =>
      createTableColumn<T>({
        columnId: column.key,

        renderHeaderCell: () => {
          const isSorted = sortedColumn?.startsWith(column.key);
          const isSortedDesc = sortedColumn === `${column.key} Desc`;
          const label = (
            <>
              <span>{column.name ?? column.key}</span>
              {column.hint ? <ColumnHint hint={column.hint} /> : null}
            </>
          );

          if (!column.isSortable) {
            return <div className={styles.headerLabel}>{label}</div>;
          }

          return (
            <div
              className={styles.headerLabel}
              style={{ cursor: 'pointer' }}
              onClick={() => handleColumnClick(column.key)}
            >
              {label}
              <span style={{ marginLeft: 4 }}>
                {isSorted ? (
                  isSortedDesc ? (
                    <ArrowSortDown20Filled />
                  ) : (
                    <ArrowSortUp20Filled />
                  )
                ) : (
                  <ArrowSort20Regular style={{ opacity: 0.5 }} />
                )}
              </span>
            </div>
          );
        },
        renderCell: (item) => <CellContent column={column} item={item} />,
      })
    );

    if (hasRowMenu) {
      dataCols.push(
        createTableColumn<T>({
          columnId: '__rowActions__',
          renderHeaderCell: () => '',
          renderCell: (item) => (
            <Button
              appearance="subtle"
              size="small"
              icon={<MoreVertical20Regular />}
              aria-label="Row actions"
              onClick={(e) => openRowMenu(e, item)}
              style={{ minWidth: 0, padding: '4px' }}
            />
          ),
        })
      );
    }

    return dataCols;
  }, [
    visibleColumns,
    sortedColumn,
    handleColumnClick,
    hasRowMenu,
    openRowMenu,
    styles.headerLabel,
  ]);

  const columnSizingOptions = useMemo(() => {
    const sizing: Record<
      string,
      { minWidth?: number; defaultWidth?: number; idealWidth?: number }
    > = {};
    visibleColumns.forEach((col) => {
      if (col.width === undefined && col.minWidth === undefined) return;
      const width = col.width ?? col.minWidth;
      sizing[col.key] = {
        minWidth: col.minWidth ?? col.width,
        defaultWidth: width,
        idealWidth: width,
      };
    });
    if (hasRowMenu) {
      sizing['__rowActions__'] = {
        minWidth: 25,
        defaultWidth: 25,
        idealWidth: 25,
      };
    }
    return sizing;
  }, [visibleColumns, hasRowMenu]);

  // Loading state
  if (loading) {
    return (
      <LoadingSign
        message={loadingConfig?.message}
        icon={loadingConfig?.icon}
        height={height}
      />
    );
  }

  // Empty state
  if (items.length === 0) {
    return renderEmptyState(empty);
  }

  const lastColumnId = visibleColumns[visibleColumns.length - 1]?.key;
  const stretchLastColumn = !fillContainer;

  return (
    <FluentProvider
      theme={theme}
      style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
    >
      <div
        className={styles.scroll}
        style={height ? { height, overflowY: 'auto' } : undefined}
      >
        <DataGrid
          items={items}
          columns={gridColumns}
          getRowId={(item) => getRowId(item)}
          resizableColumns={resizableColumns}
          resizableColumnsOptions={{ autoFitColumns: fillContainer }}
          columnSizingOptions={columnSizingOptions}
          selectionMode={selectionMode}
          selectedItems={selectedIds}
          onSelectionChange={(_, data) =>
            handleSelectionChange(data.selectedItems)
          }
          className={fillContainer ? styles.gridFill : styles.gridFree}
          style={
            fillContainer
              ? { width: '100%', maxWidth: '100%' }
              : { width: 'max-content', minWidth: '100%' }
          }
        >
          <DataGridHeader className={styles.header}>
            <DataGridRow>
              {({ renderHeaderCell, columnId }) => {
                if (columnId === '__rowActions__') {
                  return (
                    <DataGridHeaderCell className={styles.rowActionsHeader}>
                      {renderHeaderCell()}
                    </DataGridHeaderCell>
                  );
                }
                const isLast = stretchLastColumn && columnId === lastColumnId;
                return (
                  <DataGridHeaderCell
                    className={
                      isLast
                        ? `${styles.headerCell} ${styles.lastColumn}`
                        : styles.headerCell
                    }
                  >
                    {renderHeaderCell()}
                  </DataGridHeaderCell>
                );
              }}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<T>>
            {({ item, rowId }) => (
              <DataGridRow<T>
                key={rowId}
                onDoubleClick={
                  onRowDoubleClick ? () => onRowDoubleClick(item) : undefined
                }
                style={onRowDoubleClick ? { cursor: 'pointer' } : undefined}
              >
                {({ renderCell, columnId }) => {
                  if (columnId === '__rowActions__') {
                    return (
                      <DataGridCell className={styles.rowActionsCell}>
                        {renderCell(item)}
                      </DataGridCell>
                    );
                  }
                  const isLast = stretchLastColumn && columnId === lastColumnId;
                  return (
                    <DataGridCell
                      className={isLast ? styles.lastColumn : undefined}
                    >
                      {renderCell(item)}
                    </DataGridCell>
                  );
                }}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </div>
      {hasRowMenu && (
        <Menu
          open={menuOpen}
          onOpenChange={(_, data) => setMenuOpen(data.open)}
          positioning={{
            target: menuVirtualTarget ?? undefined,
            position: 'below',
            align: 'start',
          }}
        >
          <MenuTrigger disableButtonEnhancement>
            <span style={{ display: 'none' }} />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {rowMenuOptions!.map((option) => {
                const isDisabled =
                  typeof option.disabled === 'function'
                    ? menuItem
                      ? option.disabled(menuItem)
                      : true
                    : option.disabled;
                return (
                  <MenuItem
                    key={option.key}
                    icon={option.icon}
                    disabled={isDisabled}
                    onClick={() => handleMenuOptionClick(option)}
                  >
                    {option.label}
                  </MenuItem>
                );
              })}
            </MenuList>
          </MenuPopover>
        </Menu>
      )}
    </FluentProvider>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface CellContentProps<T> {
  column: DataGridColumn<T>;
  item: T;
}

function CellContent<T>({ column, item }: CellContentProps<T>) {
  if (column.renderCell) {
    return <div style={{ width: '100%' }}>{column.renderCell(item)}</div>;
  }

  let displayValue: string;
  if (column.value) {
    displayValue = column.value(item);
  } else {
    const value = (item as Record<string, unknown>)[column.key];
    if (
      (column.type === 'date' || column.type === 'datetime') &&
      (typeof value === 'string' || value instanceof Date)
    ) {
      displayValue = formatDate(value, column.type === 'datetime', true);
    } else {
      displayValue = String(value ?? '');
    }
  }

  if (column.ellipsis) {
    return <EllipsisWithTooltip content={displayValue} />;
  }

  return <TableCellLayout>{displayValue}</TableCellLayout>;
}

export default DataGridUI;
